import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { type StripeEnv, createStripeClient, getStripeErrorMessage } from "@/lib/stripe.server";

type Result<T> = T | { error: string };

// Creates (or reuses) an Express Connect account for the bartender and returns
// a fresh AccountLink URL so they can complete onboarding on Stripe.
export const createBartenderConnectOnboarding = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({
      environment: z.enum(["sandbox", "live"]),
      return_url: z.string().url(),
      refresh_url: z.string().url(),
    }).parse(d),
  )
  .handler(async ({ data, context }): Promise<Result<{ url: string }>> => {
    try {
      const { supabase, userId } = context;
      const { data: profile, error } = await supabase
        .from("profiles")
        .select("id, role, full_name, stripe_account_id")
        .eq("id", userId)
        .maybeSingle();
      if (error || !profile) throw new Error("Profile not found.");
      if (profile.role !== "bartender") throw new Error("Only bartenders set up payouts.");

      const stripe = createStripeClient(data.environment as StripeEnv);
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

      let accountId = (profile.stripe_account_id as string | null) ?? null;

      // Validate the saved account ID: must look like acct_ AND actually exist
      // on this Stripe platform/env. If anything is off, mint a fresh one.
      if (accountId && accountId.startsWith("acct_")) {
        try {
          await stripe.accounts.retrieve(accountId);
        } catch (retrieveErr) {
          console.warn("[connect] saved stripe_account_id invalid, recreating", {
            env: data.environment,
            userId,
            accountId,
            err: getStripeErrorMessage(retrieveErr),
          });
          accountId = null;
        }
      } else if (accountId) {
        // Malformed (doesn't start with acct_) — discard.
        accountId = null;
      }

      if (!accountId) {
        const { data: userRes } = await supabase.auth.getUser();
        const email = userRes.user?.email ?? undefined;
        try {
          const acct = await stripe.accounts.create({
            type: "express",
            country: "GB",
            email,
            capabilities: {
              transfers: { requested: true },
              card_payments: { requested: true },
            },
            business_type: "individual",
            metadata: { user_id: userId },
          });
          accountId = acct.id;
        } catch (createErr) {
          console.error("[connect] accounts.create failed", {
            env: data.environment,
            userId,
            err: getStripeErrorMessage(createErr),
            raw: createErr,
          });
          throw createErr;
        }
        const { error: updateErr } = await supabaseAdmin
          .from("profiles")
          .update({ stripe_account_id: accountId, stripe_payouts_enabled: false })
          .eq("id", userId);
        if (updateErr) {
          console.error("[connect] failed to persist new stripe_account_id", {
            userId,
            accountId,
            err: updateErr.message,
          });
          throw new Error("Failed to save Stripe account id.");
        }
      }

      try {
        const link = await stripe.accountLinks.create({
          account: accountId,
          type: "account_onboarding",
          return_url: data.return_url,
          refresh_url: data.refresh_url,
        });
        return { url: link.url };
      } catch (linkErr) {
        console.error("[connect] accountLinks.create failed", {
          env: data.environment,
          accountId,
          err: getStripeErrorMessage(linkErr),
          raw: linkErr,
        });
        throw linkErr;
      }
    } catch (err) {
      return { error: getStripeErrorMessage(err) };
    }
  });

// Refreshes the bartender's Connect account state from Stripe and stores
// whether they can receive payouts (charges_enabled & payouts_enabled & details_submitted).
export const refreshBartenderConnectStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ environment: z.enum(["sandbox", "live"]) }).parse(d),
  )
  .handler(async ({ data, context }): Promise<Result<{ enabled: boolean; details_submitted: boolean }>> => {
    try {
      const { supabase, userId } = context;
      const { data: profile } = await supabase
        .from("profiles")
        .select("stripe_account_id")
        .eq("id", userId)
        .maybeSingle();
      const accountId = profile?.stripe_account_id as string | null | undefined;
      if (!accountId) return { enabled: false, details_submitted: false };

      const stripe = createStripeClient(data.environment as StripeEnv);
      const acct = await stripe.accounts.retrieve(accountId);
      const enabled = Boolean(acct.payouts_enabled && acct.details_submitted);
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      await supabaseAdmin
        .from("profiles")
        .update({ stripe_payouts_enabled: enabled })
        .eq("id", userId);
      return { enabled, details_submitted: Boolean(acct.details_submitted) };
    } catch (err) {
      return { error: getStripeErrorMessage(err) };
    }
  });

export const getBartenderConnectStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data } = await supabase
      .from("profiles")
      .select("stripe_account_id, stripe_payouts_enabled, role")
      .eq("id", userId)
      .maybeSingle();
    return {
      role: (data?.role ?? null) as string | null,
      has_account: Boolean(data?.stripe_account_id),
      enabled: Boolean(data?.stripe_payouts_enabled),
    };
  });
